import { z } from 'zod';
import {
  bodyDiagramViews,
  clinicalTestOutcomes,
  standardQuestionnaireCodes,
  type BodyDiagramView,
  type ClinicalTestOutcome,
  type QuestionnaireFieldType,
  type QuestionnaireResponseKind,
  type StandardQuestionnaireCode,
} from '../enums.js';
import { QUESTIONNAIRE_TEMPLATE_NAME_MAX } from '../questionnaire-copy.js';
import { optionalText, trimmed } from './_fields.js';

/**
 * Contenu clinique structuré d'une consultation : résultats de tests, questionnaires (intégrés et
 * personnalisés) et schémas corporels. Ce qui était trois blobs jsonb dans `consultations` en V1
 * (`examination`, `questionnaires`, les six colonnes Walton) est ici trois ressources typées.
 */

/* -------------------------------------------------------------------------- */
/* Tests cliniques                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Valeur d'un critère. Le catalogue décrit des critères binaires, mais la V1 en stockait aussi des
 * mesures et des mentions libres : les trois formes restent acceptées, le service vérifie le code.
 */
export const clinicalCriterionValueSchema = z.union([
  z.boolean(),
  z.number(),
  z.string().trim().max(500),
]);

export const clinicalCriterionSchema = z
  .object({ code: trimmed(120), value: clinicalCriterionValueSchema })
  .strict();
export type ClinicalCriterionInput = z.infer<typeof clinicalCriterionSchema>;

/**
 * Remplacement de l'ensemble des résultats de la consultation. `zone` n'est pas dans l'entrée :
 * elle est déduite du catalogue, sans quoi deux sources décriraient la même appartenance.
 */
export const clinicalTestResultsPutSchema = z
  .object({
    results: z
      .array(
        z
          .object({
            testCode: trimmed(120),
            result: z.enum(clinicalTestOutcomes),
            criteria: z.array(clinicalCriterionSchema).max(60).default([]),
            notes: optionalText(4000).optional(),
          })
          .strict(),
      )
      .max(400),
  })
  .strict();
export type ClinicalTestResultsPutInput = z.infer<typeof clinicalTestResultsPutSchema>;

export interface ClinicalTestResultDto {
  id: string;
  testCode: string;
  zone: string;
  result: ClinicalTestOutcome;
  criteria: ClinicalCriterionInput[];
  notes: string | null;
}

/* -------------------------------------------------------------------------- */
/* Questionnaires — réponses                                                   */
/* -------------------------------------------------------------------------- */

/** Réponses brutes. La forme dépend de l'instrument ou du modèle : elle est validée côté serveur. */
export const questionnaireAnswersSchema = z.record(z.string().max(120), z.unknown());
export type QuestionnaireAnswersInput = z.infer<typeof questionnaireAnswersSchema>;

/**
 * Création d'une réponse. Le score n'est jamais dans l'entrée : il est calculé par
 * `@osteopro/clinical` côté serveur, sinon un client pourrait écrire un score arbitraire.
 */
export const questionnaireResponseCreateSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('standard'),
      standardCode: z.enum(standardQuestionnaireCodes),
      answers: questionnaireAnswersSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('custom'),
      templateId: z.uuid(),
      answers: questionnaireAnswersSchema,
    })
    .strict(),
]);
export type QuestionnaireResponseCreateInput = z.infer<typeof questionnaireResponseCreateSchema>;

export const questionnaireResponseUpdateSchema = z
  .object({ answers: questionnaireAnswersSchema })
  .strict();
export type QuestionnaireResponseUpdateInput = z.infer<typeof questionnaireResponseUpdateSchema>;

export const questionnaireResponseParamSchema = z
  .object({ id: z.uuid(), responseId: z.uuid() })
  .strict();

/** Sous-score d'un instrument (HADS anxiété/dépression, les six dimensions de Walton). */
export interface QuestionnaireSubscoreDto {
  code: string;
  score: number;
  maxScore: number;
  severity: string;
  interpretationKey: string;
}

export interface QuestionnaireResponseDto {
  id: string;
  kind: QuestionnaireResponseKind;
  standardCode: StandardQuestionnaireCode | null;
  templateId: string | null;
  templateVersion: number | null;
  /**
   * Les champs **de la version avec laquelle la réponse a été saisie**, pas ceux d'aujourd'hui :
   * un libellé reformulé depuis ne doit pas changer le sens d'une ancienne réponse. Nul pour un
   * instrument intégré. Pour une réponse antérieure au versionnement (migration 0080), la version
   * n'a pas été gardée et ce sont les champs actuels du modèle.
   */
  templateFields: QuestionnaireTemplateField[] | null;
  answers: QuestionnaireAnswersInput;
  score: number | null;
  subscores: QuestionnaireSubscoreDto[] | null;
  interpretationKey: string | null;
  completedAt: string;
}

/* -------------------------------------------------------------------------- */
/* Questionnaires — modèles du cabinet                                         */
/* -------------------------------------------------------------------------- */

const fieldBase = {
  /**
   * Clé des réponses. Générée par l'éditeur à la création du champ puis jamais modifiée, même si
   * le libellé change : le praticien ne la voit pas. Les modèles d'avant gardent les leurs.
   */
  id: trimmed(64),
  label: trimmed(200),
  required: z.boolean().default(false),
};

const choiceOptions = z.array(trimmed(200)).min(1).max(50);

/**
 * Schéma de schéma : la définition d'un questionnaire du cabinet. La V1 stockait ce jsonb sans
 * aucune validation (audit §3), si bien qu'un modèle cassé ne se voyait qu'au rendu.
 */
export const questionnaireTemplateFieldSchema = z.discriminatedUnion('type', [
  z.object({ ...fieldBase, type: z.literal('text') }).strict(),
  z.object({ ...fieldBase, type: z.literal('textarea') }).strict(),
  z.object({ ...fieldBase, type: z.literal('date') }).strict(),
  z.object({ ...fieldBase, type: z.literal('toggle') }).strict(),
  z
    .object({
      ...fieldBase,
      type: z.literal('number'),
      min: z.number().optional(),
      max: z.number().optional(),
    })
    .strict(),
  z
    .object({ ...fieldBase, type: z.literal('scale'), min: z.number(), max: z.number() })
    .strict()
    .refine((f) => f.max > f.min, { message: 'max doit dépasser min' }),
  z.object({ ...fieldBase, type: z.literal('select'), options: choiceOptions }).strict(),
  z.object({ ...fieldBase, type: z.literal('radio'), options: choiceOptions }).strict(),
  z.object({ ...fieldBase, type: z.literal('checkbox'), options: choiceOptions }).strict(),
]);
export type QuestionnaireTemplateField = z.infer<typeof questionnaireTemplateFieldSchema>;

/**
 * Garde-fou de compilation : l'union ci-dessus et `questionnaireFieldTypes` décrivent le même
 * ensemble. Ajouter un type de champ d'un seul côté ne compile pas (`true` devient `never`).
 */
export type QuestionnaireFieldTypeCoverage =
  QuestionnaireTemplateField['type'] extends QuestionnaireFieldType
    ? QuestionnaireFieldType extends QuestionnaireTemplateField['type']
      ? true
      : never
    : never;

/** Les identifiants de champ sont uniques : ce sont les clés des réponses. */
const fieldsSchema = z
  .array(questionnaireTemplateFieldSchema)
  .min(1)
  .max(100)
  .refine((fields) => new Set(fields.map((f) => f.id)).size === fields.length, {
    message: 'Identifiants de champ en double',
  });

export const questionnaireTemplateCreateSchema = z
  .object({
    name: trimmed(QUESTIONNAIRE_TEMPLATE_NAME_MAX),
    description: optionalText(2000).optional(),
    anatomicalZone: optionalText(64).optional(),
    fields: fieldsSchema,
  })
  .strict();
export type QuestionnaireTemplateCreateInput = z.infer<typeof questionnaireTemplateCreateSchema>;

export const questionnaireTemplateUpdateSchema = z
  .object({
    name: trimmed(QUESTIONNAIRE_TEMPLATE_NAME_MAX).optional(),
    description: optionalText(2000).optional(),
    anatomicalZone: optionalText(64).optional(),
    fields: fieldsSchema.optional(),
    isActive: z.boolean().optional(),
  })
  .strict();
export type QuestionnaireTemplateUpdateInput = z.infer<typeof questionnaireTemplateUpdateSchema>;

export interface QuestionnaireTemplateDto {
  id: string;
  name: string;
  description: string | null;
  anatomicalZone: string | null;
  fields: QuestionnaireTemplateField[];
  version: number;
  isActive: boolean;
  /**
   * Nombre de consultations où le modèle a déjà été rempli. L'éditeur l'annonce (« elles gardent
   * leur version ») et la suppression s'en sert : au-delà de zéro, le modèle se désactive au lieu
   * de s'effacer, pour que les dossiers ne perdent rien.
   */
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Fiche d'un modèle : la même forme que la liste, `usageCount` compris. */
export type QuestionnaireTemplateDetailDto = QuestionnaireTemplateDto;

/* -------------------------------------------------------------------------- */
/* Schémas corporels                                                           */
/* -------------------------------------------------------------------------- */

export const bodyDiagramParamSchema = z
  .object({ id: z.uuid(), view: z.enum(bodyDiagramViews) })
  .strict();

/** Dessin opaque : le serveur ne l'interprète pas, il en contrôle seulement la taille. */
export const bodyDiagramPutSchema = z
  .object({ drawingData: z.record(z.string().max(200), z.unknown()) })
  .strict();
export type BodyDiagramPutInput = z.infer<typeof bodyDiagramPutSchema>;

/** Taille maximale d'un dessin, sérialisé. Au-delà, 413 `body_diagram_too_large`. */
export const BODY_DIAGRAM_MAX_BYTES = 256 * 1024;

/** Métadonnées seules : la liste des vues ne transporte jamais les dessins. */
export interface BodyDiagramSummaryDto {
  view: BodyDiagramView;
  sizeBytes: number;
  updatedAt: string;
}

export interface BodyDiagramDto extends BodyDiagramSummaryDto {
  drawingData: Record<string, unknown>;
}
